import React, {
  FC,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useFormik } from "formik";
import {
  ActionTypeEnum,
  ItemModel,
  RequestActionModel,
  LookupItemModel,
  RowStateEnum,
  PageEnum,
  CompanySettingModel,
  SystemModulesEnum,
} from "../../models";
import {
  LoadingBox,
  ButtonBox,
  ItemImageManager,
  ItemGatherManager,
  PrivilegesChecker,
  ItemUnitManager,
  ItemModifierManager,
  ItemDetailsManager,
} from "..";
import {
  getLabelName,
  getUserId,
  isSystemConfigurationModuleEnabled,
  validateNoSpaces,
  validateRequired,
  yubCombineValidationsManager,
} from "../../utils";
import { Accordion } from "react-bootstrap";
import {
  itemInitialValues,
  handleSubmit,
  handleInitializeComponent,
  handleRefreshItemComponent,
  setCompanySettingsInitialValues,
} from "./businessLogic/itemMiscellaneousBl";
import { useItemConfigs, useSystemConfigurations } from "../../hooks";
import { AccordionEventKey } from "react-bootstrap/esm/AccordionContext";
import { getItemImageById } from "../../serviceBroker/itemApiServiceBroker";
import * as Yup from "yup";
import { generateItemStore } from "./businessLogic/itemStoresBl";

const ItemRegistrationManagerComponent: FC<{
  request?: ItemModel | null;
  isRefresh: boolean;
  setIsRefresh: Dispatch<SetStateAction<boolean>>;
  onActionEvent: (o: RequestActionModel) => void;
  isFormLoading: boolean;
  setFormLoading: Dispatch<SetStateAction<boolean>>;
}> = ({
        request,
        isRefresh = false,
        setIsRefresh,
        onActionEvent = () => {},
        isFormLoading,
        setFormLoading,
      }) => {
  //#region state
  const [loading, setLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [item, setItem] = useState<ItemModel>(request ?? itemInitialValues);
  const [isImageAccordionExpanded, setIsImageAccordionExpanded] =
      useState(false);
  const isItemImageLoadedBefore = useRef(false);
  const [unitList, setUnitList] = useState<LookupItemModel[]>([]);
  const [defaultUnitId, setDefaultUnitId] = useState(0);
  const [categoryList, setCategoryList] = useState<LookupItemModel[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettingModel>(
      setCompanySettingsInitialValues
  );
  const [isRefreshUnitManager, setIsRefreshUnitManager] =
      useState<boolean>(false);
  const [isRefreshGatherManager, setIsRefreshGatherManager] =
      useState<boolean>(false);
  const categorySelectBoxMultiselectRef = useRef<any>();
  const [validationSchema] = useState(
      Yup.object({
        Name: yubCombineValidationsManager([
          validateRequired(),
          validateNoSpaces(),
        ]),
        Cat_ID: Yup.number().test("cat-id", "", function (value) {
          const { createError } = this;
          if (value === undefined || value === null || value <= 0) {
            return createError({
              message: getLabelName("categoryId.missing"),
            });
          }
          return true;
        }),
      })
  );
  const { userConfiguration, isTransactionLoaded } =
      useSystemConfigurations(setLoading);
  const { itemConfigs, isItemConfigsLoaded } = useItemConfigs();
  //#endregion
  //#region variables
  const shouldRenderContent = useMemo(() =>
          !isFormLoading && isTransactionLoaded && isItemConfigsLoaded,
      [isFormLoading, isTransactionLoaded, isItemConfigsLoaded]
  );
  //#endregion
  //#region formik
  const formik = useFormik<ItemModel>({
    initialValues: {
      ...(request ?? itemInitialValues),
      isNewItem: !(request !== null && request !== undefined),
    },
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
    // validationSchema: validationSchema,
    // validate,
    onSubmit: async (values, { setSubmitting }) => {
      const userID = getUserId();
      setFormLoading(true);
      const updatedValues = {
        ...values,
        ItemsInstores: [
          ...item.ItemsInstores.map((store) => ({
            ...store,
            Status: values.ItemsInstores[0].Status,
          })),
        ],
        HasImage: values.ItemImages !== null,
        CreatedBy: item.CreatedBy || userID,
        ModifiedBy: userID,
      };

      const updateUnitIdForItemRequest = updateUnitIdForItem(
          updatedValues,
          defaultUnitId
      );
      const itemResponse = await handleSubmit(
          updateUnitIdForItemRequest,
          userConfiguration
      );
      setFormLoading(false);
      if (
          itemResponse.Errors === null ||
          itemResponse.Errors === undefined ||
          itemResponse.Errors.length === 0
      ) {
        await handleReset();
        onActionEvent({
          request: itemResponse.Result,
          action: ActionTypeEnum.Success,
        });
      } else {
        onActionEvent({
          id: 0,
          request: itemResponse.Errors,
          action: ActionTypeEnum.RaiseError,
        });
      }
    },
  });
  //#endregion

  //#region functions with useCallback
  // Memoize functions to prevent unnecessary re-renders
  const updateUnitIdForItem = useCallback((
      item: ItemModel,
      defaultUnitId: number
  ): ItemModel => {
    return {
      ...item,
      ItemsInstores: item.ItemsInstores.map((store) => ({
        ...store,
        Item_unit: store.Item_unit.map((unit) => ({
          ...unit,
          Unit_ID:
              unit.Unit_ID == null || unit.Unit_ID == 0
                  ? defaultUnitId
                  : unit.Unit_ID,
        })),
      })),
    };
  }, []);

  const handleSetRefreshStates = useCallback(async (isRefreshed: boolean) => {
    setIsRefreshGatherManager(isRefreshed);
    setIsRefreshUnitManager(isRefreshed);
  }, []);

  const handleRequestSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    setFormLoading(true);
    e.preventDefault(); // Prevent default form submission
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      await formik.setTouched(
          Object.keys(formik.initialValues).reduce(
              (acc, field) => ({ ...acc, [field]: true }),
              {}
          )
      );
      onActionEvent({ action: ActionTypeEnum.ScrollToTop });
      setFormLoading(false);
      return;
    }
    setFormLoading(false);
    formik.handleSubmit(e);
  }, [formik, onActionEvent, setFormLoading]);

  const handleClearControls = useCallback(() => {
    categorySelectBoxMultiselectRef.current && categorySelectBoxMultiselectRef.current.clearValue();
  }, []);

  const handleReset = useCallback(async () => {
    setFormLoading(true);
    const _item: ItemModel = {
      ...itemInitialValues,
      ItemsInstores: generateItemStore(),
      TaxValue: companySettings?.DefaultTaxPercentage ?? 0,
      isNewItem: true,
    };
    await formik.setValues(_item);
    setItem(_item);
    handleClearControls();
    //formik.resetForm();
    await handleSetRefreshStates(true);
    setFormLoading(false);
  }, [companySettings, formik, handleClearControls, handleSetRefreshStates, setFormLoading]);

  const handleGetImage = useCallback(async () => {
    if (!request?.ID) return;

    setIsImageLoading(true);
    const imageData = await getItemImageById(request.ID);

    if (!imageData.Result?.File64Bit) {
      setIsImageLoading(false);
      return;
    }
    await formik.setFieldValue("ItemImages", {
      File64Bit: imageData.Result.File64Bit,
      ID: imageData.Result.ID,
      ImageDescription: imageData.Result.FileDescription,
      ImageName: imageData.Result.Name,
      Name: imageData.Result.Name,
      rowState: RowStateEnum.Update,
    });
    setIsImageLoading(false);
  }, [formik, request]);

  // Memoize handlers for child components
  const handleDeleteImage = useCallback(() => {
    if (!request?.ID)
      return formik.setFieldValue("ItemImages", null);
    formik.setFieldValue("ItemImages", {
      ...formik.values.ItemImages,
      rowState: RowStateEnum.Delete,
    });
  }, [formik, request?.ID, formik.values.ItemImages]);

  const handleSetFileBase64 = useCallback((base64: string | null) => {
    if (base64 === null)
      return formik.setFieldValue("ItemImages", null);
    formik.setFieldValue("ItemImages", {
      File64Bit: base64,
      ID: request?.ItemImages?.ID
          ? request?.ItemImages?.ID
          : 0,
      ImageDescription: `${formik.values.Name} item`,
      ImageName: `${formik.values.Name} item`,
      Name: formik.values.Name,
      rowState: request?.ID
          ? RowStateEnum.Update
          : RowStateEnum.Add,
    });
  }, [formik, request?.ID, request?.ItemImages?.ID, formik.values.Name]);
  //#endregion
  //#region useEffect
  useEffect(() => {
    const fillData = async () => {
      setFormLoading(true);
      await handleInitializeComponent(
          formik,
          setFormLoading,
          setUnitList,
          setCategoryList,
          setCompanySettings,
          setDefaultUnitId
      );
      setFormLoading(false);
    };
    fillData().then(() => {});
  }, []);

  useEffect(() => {
    if (isRefresh) {
      const fillData = async () => {
        setFormLoading(true);
        await handleRefreshItemComponent(
            formik,
            setItem,
            setIsRefresh,
            request ?? itemInitialValues
        );
        setIsRefresh(false);
        setIsImageAccordionExpanded(false);
        isItemImageLoadedBefore.current = false;
        setFormLoading(false);
      };
      fillData().then(() => {});
    }
  }, [isRefresh, formik, request, setIsRefresh, setFormLoading]);

  useEffect(() => {
    const fillData = async () => {
      if (
          isItemImageLoadedBefore.current ||
          !isImageAccordionExpanded ||
          !request?.ID
      )
        return;
      setFormLoading(false);
      await handleGetImage();
      isItemImageLoadedBefore.current = true;
      setFormLoading(false);
    };
    fillData().then(() => {});
  }, [isImageAccordionExpanded, request?.ID, handleGetImage, setFormLoading]);
  //#endregion
  // Memoize props passed to child components
  const itemDetailsProps = useMemo(() => ({
    formik,
    item,
    categoryList,
    itemConfigs,
    userConfiguration,
    categorySelectBoxMultiselectRef
  }), [formik, item, categoryList, itemConfigs, userConfiguration]);

  const itemUnitManagerProps = useMemo(() => ({
    userConfiguration,
    setState: setItem,
    state: item,
    taxValue: formik.values.TaxValue ?? 0,
    unitList,
    onActionEvent: () => {},
    setIsRefresh: setIsRefreshUnitManager,
    isRefresh: isRefreshUnitManager
  }), [userConfiguration, item, formik.values.TaxValue, unitList, isRefreshUnitManager]);

  const itemModifierManagerProps = useMemo(() => ({
    setState: setItem,
    state: item,
    onActionEvent: () => {}
  }), [item]);

  const itemGatherManagerProps = useMemo(() => ({
    categoryList,
    setState: setItem,
    state: item,
    taxValue: formik.values.TaxValue ?? 0,
    setIsRefresh: setIsRefreshGatherManager,
    isRefresh: isRefreshGatherManager
  }), [categoryList, item, formik.values.TaxValue, isRefreshGatherManager]);

  const itemImageManagerProps = useMemo(() => ({
    isImageLoading,
    image: formik.values.ItemImages,
    deleteImage: handleDeleteImage,
    setFileBase64: handleSetFileBase64
  }), [isImageLoading, formik.values.ItemImages, handleDeleteImage, handleSetFileBase64]);

  // Determine if the system configuration module is enabled (memoized)
  const isSharedPOSRestaurantEnabled = useMemo(() =>
          isSystemConfigurationModuleEnabled(
              userConfiguration.systemModulesConfigurations,
              (p) => p.ID === SystemModulesEnum.Shared_POS_and_Restaurant
          ),
      [userConfiguration.systemModulesConfigurations]
  );

  //#region html

  return (
    <>
      <LoadingBox isLoading={!shouldRenderContent}>
        {shouldRenderContent ? (
          <form className="forms-sample" onSubmit={handleRequestSubmit}>
            <div
              //className="col-12 col-md-2 gap-3 d-flex justify-content-end sticky-buttons"
              style={{
                position: "sticky",
                top: "60px",
                zIndex: 1,
                background: "rgb(249, 247, 247)",
                padding: "10px", // Optional: Add padding for spacing
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", // Optional: Add shadow for separation
                marginBottom:"6px",
                borderRadius:"10px"
              }}
            >
              <PrivilegesChecker
                formId={PageEnum.Items}
                action={request?.ID ? "EnableUpdate" : "EnableSave"}
              >
                <ButtonBox
                  iconType="content-save"
                  type="submit"
                  className="btn  btn-sm mx-2"
                  variant="primary"
                >
                  {getLabelName(request?.ID ? "Update" : "Save")}
                </ButtonBox>
              </PrivilegesChecker>
              <ButtonBox
                iconType="receipt"
                variant="danger"
                type="button"
                className="btn btn-sm"
                onClick={async () => {
                  await handleReset();
                  onActionEvent({
                    action: ActionTypeEnum.Clear,
                  });
                }}
              >
                {getLabelName("New")}
              </ButtonBox>
            </div>
            <Accordion defaultActiveKey="0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>{getLabelName("Items")}</Accordion.Header>
                <Accordion.Body>
                  <ItemDetailsManager
                    formik={formik}
                    item={item}
                    categoryList={categoryList}
                    itemConfigs={itemConfigs}
                    userConfiguration={userConfiguration}
                    categorySelectBoxMultiselectRef={
                      categorySelectBoxMultiselectRef
                    }
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            <Accordion defaultActiveKey="0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>{getLabelName("Units")}</Accordion.Header>
                <Accordion.Body className="overflow-visible">
                  <ItemUnitManager
                    userConfiguration={userConfiguration}
                    setState={setItem}
                    state={item}
                    taxValue={formik.values.TaxValue ?? 0}
                    unitList={unitList}
                    onActionEvent={() => {}}
                    setIsRefresh={setIsRefreshUnitManager}
                    isRefresh={isRefreshUnitManager}
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            {itemConfigs.ShowModifiers && (
              <Accordion>
                <Accordion.Item eventKey="2">
                  <Accordion.Header>
                    {getLabelName("Modifiers")}
                  </Accordion.Header>
                  <Accordion.Body>
                    <ItemModifierManager
                      setState={setItem}
                      state={item}
                      onActionEvent={() => {}}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            )}

                {(itemConfigs.ShowGatherItem || itemConfigs.ShowproductionItem) && (
                    <Accordion defaultActiveKey="0">
                      <Accordion.Item eventKey="1">
                        <Accordion.Header>
                          {getLabelName("Gather Item")}
                        </Accordion.Header>
                        <Accordion.Body className="overflow-visible">
                          <ItemGatherManager {...itemGatherManagerProps} />
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                )}
                {isSharedPOSRestaurantEnabled && (
                    <Accordion
                        onSelect={(activeKey: AccordionEventKey) =>
                            setIsImageAccordionExpanded(activeKey !== null)
                        }
                        activeKey={isImageAccordionExpanded ? "3" : null}
                    >
                      <Accordion.Item eventKey="3">
                        <Accordion.Header>
                          {getLabelName("Image Item")}
                        </Accordion.Header>
                        <Accordion.Body
                            className="overflow-visible position-relative"
                            style={{ minHeight: 200 }}
                        >
                          <ItemImageManager {...itemImageManagerProps} />
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                )}
              </form>
          ) : null}
        </LoadingBox>
      </>
  );
  //#endregion
};

// Export memoized component
export const ItemRegistrationManager = React.memo(ItemRegistrationManagerComponent);